import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ComposedChart, Area, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useBrand } from "@/context/BrandContext";
import { formatMoney } from "@/lib/format";
import { moneyTick, CATEGORY_COLORS } from "@/lib/charts";
import { PageHeader } from "@/components/common/PageHeader";
import { PeriodFilter } from "@/components/common/PeriodFilter";
import { StatCard } from "@/components/common/StatCard";
import { ChartCard } from "@/components/common/ChartCard";
import { Skeleton } from "@/components/ui/skeleton";

export default function FinancialDashboard() {
  const { t } = useT();
  const { brandParam } = useBrand();
  const navigate = useNavigate();
  const [period, setPeriod] = useState({ preset: "this_month" });
  const [sortKey, setSortKey] = useState("facturacion_cents");

  const { data, isLoading } = useQuery({
    queryKey: ["dash-financial", brandParam, period.desde, period.hasta],
    queryFn: async () => (await api.get("/dashboards/financial", {
      params: { brand: brandParam, desde: period.desde, hasta: period.hasta },
    })).data,
  });

  if (isLoading || !data) {
    return (
      <div className="p-6"><div className="grid gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[92px] rounded-[10px]" />)}
      </div></div>
    );
  }

  const gapData = data.cobros_vs_pagos_mensual.map((m) => ({
    ...m, base: Math.min(m.cobros_cents, m.pagos_cents), gap: Math.abs(m.cobros_cents - m.pagos_cents),
  }));
  const tablaOrdenada = [...data.facturacion_por_edificio].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));

  return (
    <>
      <PageHeader testId="dash-financial-header" title={t("dashboards.financial.title")}
        subtitle={t("dashboards.financial.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6" data-testid="dash-financial-page">
        <div className="mb-4"><PeriodFilter value={period} onChange={setPeriod} /></div>

        <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard testId="dash-fin-receivable" label={t("dashboards.financial.receivable")} value={formatMoney(data.total_por_cobrar_cents)} />
          <StatCard testId="dash-fin-payable" label={t("dashboards.financial.payable")} value={formatMoney(data.total_por_pagar_subs_cents)} />
          <StatCard testId="dash-fin-unbilled" label={t("dashboards.financial.unbilled")} value={formatMoney(data.monto_cerrado_sin_facturar_cents)} />
          <StatCard testId="dash-fin-avg-days" label={t("dashboards.financial.avg_billing_days")} value={`${data.dias_promedio_facturacion} d`} />
        </div>

        {!!data.no_liquidable_cantidad && (
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard testId="dash-fin-not-liquidable" warn
              label={t("dashboards.financial.not_liquidable")}
              value={formatMoney(data.no_liquidable_monto_cents)}
              onClick={() => navigate("/ordenes?vista=sin_poder_liquidar")} />
          </div>
        )}

        <div className="grid gap-4 xl:grid-cols-2">
          <ChartCard testId="dash-fin-aging" title={t("dashboards.financial.aging_title")} height={220} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.aging_tramos.map((tr) => (
                <tr key={tr.tramo} className="border-b border-line">
                  <td className="px-2 py-1">{tr.tramo}</td><td className="px-2 py-1 text-right">{formatMoney(tr.monto_cents)}</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={[Object.fromEntries(data.aging_tramos.map((tr) => [tr.tramo, tr.monto_cents]))]} layout="vertical">
                <XAxis type="number" tickFormatter={moneyTick} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis type="category" hide />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {data.aging_tramos.map((tr, i) => (
                  <Bar isAnimationActive={false} key={tr.tramo} dataKey={tr.tramo} stackId="a" minPointSize={2}
                    fill={["var(--chart-seq-1)", "var(--chart-seq-2)", "var(--chart-seq-3)", "var(--chart-seq-4)"][i % 4]} radius={4} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard testId="dash-fin-sub-balances" title={t("dashboards.financial.sub_balances")} height={Math.max(180, data.saldos_por_sub.length * 32)} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.saldos_por_sub.map((s) => (
                <tr key={s.sub_nombre} className="border-b border-line">
                  <td className="px-2 py-1">{s.sub_nombre}</td><td className="px-2 py-1 text-right">{formatMoney(s.saldo_cents)}</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={Math.max(180, data.saldos_por_sub.length * 32)}>
              <BarChart data={data.saldos_por_sub} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                <XAxis type="number" tickFormatter={moneyTick} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis type="category" dataKey="sub_nombre" width={95} tick={{ fontSize: 10.5, fill: "var(--ink-secondary)" }} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Bar isAnimationActive={false} dataKey="saldo_cents" fill={CATEGORY_COLORS[0]} radius={4} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard testId="dash-fin-cash-gap" title={t("dashboards.financial.cash_gap_title")} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.cobros_vs_pagos_mensual.map((m) => (
                <tr key={m.mes} className="border-b border-line">
                  <td className="px-2 py-1">{m.mes}</td>
                  <td className="px-2 py-1 text-right">{formatMoney(m.cobros_cents)}</td>
                  <td className="px-2 py-1 text-right">{formatMoney(m.pagos_cents)}</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={gapData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis tickFormatter={moneyTick} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area isAnimationActive={false} dataKey="base" stackId="gap" stroke="none" fill="transparent" legendType="none" />
                <Area isAnimationActive={false} dataKey="gap" stackId="gap" stroke="none" fill="var(--state-warn-soft)" fillOpacity={0.6} name="Desfase" />
                <Line isAnimationActive={false} type="monotone" dataKey="cobros_cents" name={t("dashboards.financial.collections")} stroke={CATEGORY_COLORS[0]} strokeWidth={2} dot={false} />
                <Line isAnimationActive={false} type="monotone" dataKey="pagos_cents" name={t("dashboards.financial.payments")} stroke={CATEGORY_COLORS[1]} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard testId="dash-fin-billing-days" title={t("dashboards.financial.billing_days_evolution")} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.evolucion_dias_facturacion.map((m) => (
                <tr key={m.mes} className="border-b border-line">
                  <td className="px-2 py-1">{m.mes}</td><td className="px-2 py-1 text-right">{m.promedio_dias} d</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={data.evolucion_dias_facturacion}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <Tooltip formatter={(v) => `${v} d`} />
                <Line isAnimationActive={false} type="monotone" dataKey="promedio_dias" stroke={CATEGORY_COLORS[6]} strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="mt-4 rounded-[10px] border border-line bg-white" data-testid="dash-fin-buildings-table">
          <p className="border-b border-line px-4 py-2.5 text-[13px] font-semibold text-ink">
            {t("dashboards.financial.buildings_table")}
          </p>
          <table className="w-full text-[12.5px]">
            <thead className="bg-surface-sunken">
              <tr className="text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
                {[["property_nombre", "Edificio"], ["facturacion_cents", "Facturación"], ["costo_cents", "Costo"],
                 ["margen_cents", "Margen"], ["margen_pct", "Margen %"]].map(([k, l]) => (
                  <th key={k} onClick={() => setSortKey(k)} className="cursor-pointer px-3 py-2 text-left hover:text-ink">{l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tablaOrdenada.map((e) => (
                <tr key={e.property_nombre} className="border-b border-line">
                  <td className="px-3 py-1.5">{e.property_nombre}</td>
                  <td className="px-3 py-1.5">{formatMoney(e.facturacion_cents)}</td>
                  <td className="px-3 py-1.5">{formatMoney(e.costo_cents)}</td>
                  <td className="px-3 py-1.5">{formatMoney(e.margen_cents)}</td>
                  <td className="px-3 py-1.5">{e.margen_pct === null ? t("wo.margin_na") : `${e.margen_pct}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
